import { useState, useCallback, useMemo } from 'react';
import { 
  CurrencyService, 
  CurrencyConversionRequest, 
  CurrencyConversionResult,
  CurrencyRate 
} from '../api/services/currencyService';
import { useGateway } from './useGateway';

interface UseCurrencyReturn {
  convertCurrency: (request: CurrencyConversionRequest) => Promise<CurrencyConversionResult>;
  getRate: (fromCurrency: string, toCurrency: string) => Promise<CurrencyRate>;
  clearCache: () => void;
  clearCacheForPair: (fromCurrency: string, toCurrency: string) => void;
  loading: boolean;
  error: string | null;
}

export const useCurrency = (): UseCurrencyReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { gateway } = useGateway();
  
  const currencyService = useMemo(() => new CurrencyService(gateway.apiClient), [gateway]);

  const convertCurrency = useCallback(async (request: CurrencyConversionRequest): Promise<CurrencyConversionResult> => {
    setLoading(true);
    setError(null);

    try {
      const result = await currencyService.convertCurrency(request);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to convert currency';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currencyService]);

  const getRate = useCallback(async (fromCurrency: string, toCurrency: string): Promise<CurrencyRate> => {
    setLoading(true);
    setError(null);

    try {
      const result = await currencyService.getRate(fromCurrency, toCurrency);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get currency rate';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currencyService]);

  const clearCache = useCallback(() => {
    currencyService.clearCache();
  }, [currencyService]);

  const clearCacheForPair = useCallback((fromCurrency: string, toCurrency: string) => {
    currencyService.clearCacheForPair(fromCurrency, toCurrency);
  }, [currencyService]);

  return {
    convertCurrency,
    getRate,
    clearCache,
    clearCacheForPair,
    loading,
    error
  };
};
